/**
 * consent.ts — GTM Consent Mode v2 (UK PECR / UK-GDPR) for the A1 install.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: tracking A1 (consent).
 *
 * WHY: on a UK health app, analytics/ads storage is non-essential, so it is
 * OPT-IN — nothing may be stored until the visitor agrees. This module drives
 * Google Consent Mode v2:
 *   • `initConsent()` pushes the DEFAULT state (every non-essential type DENIED)
 *     BEFORE the GTM container loads, so from the very first hit GTM/GA4 send
 *     only cookieless, consent-respecting pings and set no ad/analytics cookies.
 *   • `setConsent()` (from the banner's Accept/Reject) pushes a Consent Mode
 *     UPDATE and records the choice server-side, so the banner does not reshow
 *     and a returning visitor's grant is re-applied on the next load.
 *
 * THE CONSENT BRIDGE (gateway#34 shape). The decision has to reach the server so
 * the Part D purchase conversion can be gated on it. A `gps_consent` cookie the
 * client writes itself is FORGEABLE (non-httpOnly, apex-scoped — any script on
 * any *.thegpservice.co.uk host could set `granted`), so we do NOT write it
 * here. Instead the banner POSTs the decision to `POST {gateway}/v1/consent`,
 * and the GATEWAY mints a SIGNED `gps_consent` cookie that only it can produce
 * and that it verifies before relaying downstream (`x-gps-consent`). This module
 * still READS that cookie back (to re-apply Consent Mode and not reshow the
 * banner) — it just cannot forge a new decision. The pre-bridge builds wrote a
 * host-only unsigned `gps_consent`; we expire that legacy variant so the signed
 * record is the only one.
 *
 * Consent Mode v2 covers `ad_user_data` + `ad_personalization` alongside
 * `ad_storage` / `analytics_storage`. `functionality_storage` +
 * `security_storage` stay granted (essential). The commands are pushed via the
 * canonical gtag `arguments` form onto the SAME `window.dataLayer` the GTM
 * loader + funnel events use.
 */
import { GATEWAY_URL } from '../config';

/** The signed consent record — read here, written ONLY by the gateway. */
const CONSENT_COOKIE = 'gps_consent';
/** The gateway write lane the banner POSTs its decision to (gateway#34). */
const CONSENT_ENDPOINT = `${GATEWAY_URL}/v1/consent`;

/** A visitor's category-level decision. Undecided is represented as `null`. */
export interface ConsentDecision {
  analytics: boolean;
  ads: boolean;
}

type ConsentSignal = 'granted' | 'denied';
const GRANTED: ConsentSignal = 'granted';
const DENIED: ConsentSignal = 'denied';

interface ConsentWindow {
  dataLayer?: unknown[];
}

/** The GTM dataLayer, created lazily (shared with dataLayer.ts / gtm.ts). */
function dataLayer(): unknown[] {
  if (typeof window === 'undefined') return [];
  const w = window as unknown as ConsentWindow;
  if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
  return w.dataLayer;
}

/**
 * The canonical gtag pusher. GTM's Consent API reads the raw `arguments` object
 * (not a plain array), exactly as Google's gtag.js emits it — so this pushes
 * `arguments`, never a rewritten array.
 */
const gtag: (...args: unknown[]) => void = function gtag() {
  // eslint-disable-next-line prefer-rest-params
  dataLayer().push(arguments);
};

/** base64url → binary string (the gateway signs `base64url(json).sig`). */
function b64urlDecode(s: string): string {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  return atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
}

/** Decode ONE gateway-signed cookie value into a decision, or null when it is
 * not the signed shape (a stale unsigned legacy value, or malformed). We read
 * the payload only — the signature is the GATEWAY's to verify server-side; here
 * we just need the two booleans to drive Consent Mode and the banner. */
function parseSignedConsent(value: string): ConsentDecision | null {
  const dot = value.indexOf('.');
  if (dot <= 0) return null; // no payload.sig shape → legacy/unsigned, treat as undecided
  try {
    const claims = JSON.parse(b64urlDecode(value.slice(0, dot))) as { a?: unknown; d?: unknown };
    if ((claims.a === 0 || claims.a === 1) && (claims.d === 0 || claims.d === 1)) {
      return { analytics: claims.a === 1, ads: claims.d === 1 };
    }
    return null;
  } catch {
    return null;
  }
}

/** Read the gateway-signed decision back, or `null` when the visitor has not
 * chosen yet. Scans every `gps_consent` entry and returns the first that parses
 * as signed, so a lingering legacy host-only cookie never shadows the real one. */
export function readStoredConsent(): ConsentDecision | null {
  if (typeof document === 'undefined') return null;
  for (const c of document.cookie.split('; ')) {
    if (!c.startsWith(`${CONSENT_COOKIE}=`)) continue;
    const decision = parseSignedConsent(decodeURIComponent(c.slice(CONSENT_COOKIE.length + 1)));
    if (decision) return decision;
  }
  return null;
}

/** Record the decision server-side: the gateway mints the SIGNED cookie (only
 * it holds the key). `credentials: 'include'` so the cross-origin marketing
 * banner receives the Set-Cookie; `keepalive` so it still sends if the click
 * navigates away. Fail-soft: a network error or an unconfigured gateway (503)
 * just means the banner may reshow — deny-first server-side, never a forged
 * local cookie. Consent Mode is already applied locally by the caller, so the
 * UX is not blocked on this. */
function postConsent(decision: ConsentDecision): void {
  if (typeof fetch === 'undefined') return;
  void fetch(CONSENT_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ analytics: decision.analytics, ads: decision.ads }),
    keepalive: true,
  }).catch(() => undefined);
}

/** Expire the PRE-BRIDGE host-only `gps_consent` (unsigned JSON, written by the
 * old client-side code). It is a SEPARATE cookie from the gateway's apex-scoped
 * signed one of the same name; omitting `Domain` targets only the host-only
 * variant, so the signed record is left intact and can never be shadowed. */
function expireLegacyConsentCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/** Push a Consent Mode UPDATE reflecting a decision (does NOT persist). */
function applyConsent(decision: ConsentDecision): void {
  gtag('consent', 'update', {
    ad_storage: decision.ads ? GRANTED : DENIED,
    ad_user_data: decision.ads ? GRANTED : DENIED,
    ad_personalization: decision.ads ? GRANTED : DENIED,
    analytics_storage: decision.analytics ? GRANTED : DENIED,
  });
}

/**
 * Push the Consent Mode v2 DEFAULT state (deny every non-essential type) — MUST
 * run BEFORE the GTM container loads. If the visitor already chose, their grant
 * is re-applied immediately so tags fire on this load too.
 */
export function initConsent(): void {
  gtag('consent', 'default', {
    ad_storage: DENIED,
    ad_user_data: DENIED,
    ad_personalization: DENIED,
    analytics_storage: DENIED,
    functionality_storage: GRANTED,
    security_storage: GRANTED,
    wait_for_update: 500,
  });
  // Belt-and-braces with denied ad_storage: redact ad data until opt-in.
  gtag('set', 'ads_data_redaction', true);
  const stored = readStoredConsent();
  if (stored) applyConsent(stored);
}

/** Record the visitor's choice: apply Consent Mode locally (immediate, so tags
 * fire this load), ask the gateway to record the SIGNED cookie, and expire any
 * pre-bridge host-only cookie so one record remains. */
export function setConsent(decision: ConsentDecision): void {
  applyConsent(decision);
  postConsent(decision);
  expireLegacyConsentCookie();
}

/** Banner "Accept all" — grant analytics + ads. */
export function acceptAllConsent(): void {
  setConsent({ analytics: true, ads: true });
}

/** Banner "Reject all" — keep only the essential (granted-by-default) storage. */
export function rejectAllConsent(): void {
  setConsent({ analytics: false, ads: false });
}
